import {
  GLOBALIZATION_INTL,
  GLOBALIZATION_NUMBER_TYPES,
  GLOBALIZATION_SUBNET_INTL,
} from "@/react/features/globalization/constants";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import oktaAuth from "@/okta-auth";
import { getEnv } from "@/env";
import downloadBlob from "@/react/downloadBlob";

const baseUrl = getEnv("VUE_APP_GLOBALIZATION_BASE_ENDPOINT");

const baseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers) => {
    // By default, if we have a token in the store, let's use that for authenticated requests
    const token = oktaAuth.getAccessToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

const subnetListTransform = (response) => {
  return response.map((subnet) => {
    return {
      id: subnet.id,
      region: subnet.region.name,
      service: subnet.service.name,
      subnetString: subnet.subnet + "/" + subnet.netmask,
      portUsage: subnet.portUsage.port_usage,
      QOS: subnet.portUsage.QOS,
      ACL: subnet.portUsage.ACL,
      is_proxy: subnet.portUsage.is_proxy,
      is_non_proxy: subnet.portUsage.is_non_proxy,
      is_data_center: subnet.serviceSubType?.is_data_center,
    };
  });
};

export const globalizationApi = createApi({
  reducerPath: "globalizationApi",
  baseQuery,
  endpoints: (builder) => ({
    getAllFromGlobalization: builder.query({
      query: () => ({
        url: "international",
        method: "GET",
      }),
      providesTags: [GLOBALIZATION_INTL],
    }),
    getSubnetList: builder.query({
      query: (arg) => {
        const endpoint = {
          url: "subnet",
          method: "GET",
        };
        if (arg?.type) {
          endpoint.params = { type: arg.type };
        }
        return endpoint;
      },
      providesTags: [GLOBALIZATION_SUBNET_INTL],
      transformResponse: (response) => {
        return subnetListTransform(response);
      },
    }),
    getPortUsage: builder.query({
      query: () => ({
        url: "port-usage",
        method: "GET",
      }),
      providesTags: [GLOBALIZATION_INTL],
    }),
    //TODO: Merge into getAllFromGlobalization
    getInternationalByIds: builder.query({
      query: (arg) => {
        const url = "international";
        const endpoint = {
          url,
          method: "GET",
        };
        if (arg?.idList) {
          endpoint.url += "/" + arg.idList.join(",");
        }
        return endpoint;
      },
      providesTags: [GLOBALIZATION_INTL],
    }),
    getRights: builder.query({
      query: () => {
        const url = "rights/current";
        return {
          url,
          method: "GET",
        };
      },
      providesTags: [GLOBALIZATION_INTL],
    }),
    updateInternational: builder.mutation({
      query: (args) => ({
        url: "international/" + args.id,
        method: "PATCH",
        body: args.payload,
      }),
      invalidatesTags: [GLOBALIZATION_INTL],
    }),
    getNumberTypes: builder.query({
      query: () => ({
        url: "number_type",
        method: "GET",
      }),
      providesTags: [GLOBALIZATION_NUMBER_TYPES],
    }),
    deleteNumberingDetails: builder.mutation({
      query: (id) => ({
        url: "international_details/" + id,
        method: "DELETE",
      }),
      invalidatesTags: [GLOBALIZATION_INTL],
    }),
    updateNumberingDetails: builder.mutation({
      query: (args) => ({
        url: "international_details/" + args.id,
        method: "PATCH",
        body: args.payload,
      }),
      invalidatesTags: [GLOBALIZATION_INTL],
    }),
    createNumberingDetails: builder.mutation({
      query: (args) => ({
        url: "international_details",
        method: "POST",
        body: args,
      }),
      invalidatesTags: [GLOBALIZATION_INTL],
    }),
    deleteLNP: builder.mutation({
      query: (id) => ({
        url: "lnp_details/" + id,
        method: "DELETE",
      }),
      invalidatesTags: [GLOBALIZATION_INTL],
    }),
    updateLNP: builder.mutation({
      query: (args) => ({
        url: "lnp_details/" + args.id,
        method: "PATCH",
        body: args.payload,
      }),
      invalidatesTags: [GLOBALIZATION_INTL],
    }),
    createLNP: builder.mutation({
      query: (args) => ({
        url: "lnp_details",
        method: "POST",
        body: args,
      }),
      invalidatesTags: [GLOBALIZATION_INTL],
    }),
    createNumberType: builder.mutation({
      query: (args) => ({
        url: "number_type",
        method: "POST",
        body: args,
      }),
      invalidatesTags: [GLOBALIZATION_NUMBER_TYPES],
    }),
  }),
});

export const fetchAndDownloadBlob = async (url) => {
  try {
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${oktaAuth.getAccessToken()}` },
    });
    if (!response.ok) {
      console.error("Download failed");
      return false;
    }
    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = "downloaded-file";
    if (contentDisposition && contentDisposition.includes("attachment")) {
      const matches = contentDisposition.match(/filename="(.+)"/);
      if (matches && matches[1]) {
        filename = matches[1];
      }
    }
    const blob = await response.blob();
    downloadBlob({ blob, filename });
    return true;
  } catch (error) {
    return false;
  }
};

export const globalizationDownload = async (url) => {
  return fetchAndDownloadBlob(
    getEnv("VUE_APP_GLOBALIZATION_BASE_ENDPOINT") + "/" + url
  );
};

export const {
  useCreateNumberTypeMutation,
  useCreateLNPMutation,
  useUpdateLNPMutation,
  useDeleteLNPMutation,
  useCreateNumberingDetailsMutation,
  useUpdateNumberingDetailsMutation,
  useDeleteNumberingDetailsMutation,
  useUpdateInternationalMutation,
  useGetAllFromGlobalizationQuery,
  useGetSubnetListQuery,
  useGetPortUsageQuery,
  useGetInternationalByIdsQuery,
  useGetRightsQuery,
  useLazyGetRightsQuery,
  useLazyGetNumberTypesQuery,
} = globalizationApi;
